<template>
  <Select
    :model-value="String(modelValue)"
    @update:model-value="handleChange"
    :disabled="loading || disabled"
  >
    <SelectTrigger :class="triggerClass" :style="{ width: width, maxWidth: maxWidth }">
      <SelectValue :placeholder="placeholder" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem
        v-for="option in pageOptions"
        :key="option.value"
        :value="String(option.value)"
        :disabled="option.disabled"
      >
        {{ option.label }}
      </SelectItem>
    </SelectContent>
  </Select>
</template>

<script setup>
import { computed } from 'vue'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

const PAGE_SIZES = {
    SMALL: [5, 10],
    STANDARD: [10, 20, 50],
    LARGE: [100, 200, 500]
}

const props = defineProps({
    modelValue: {
        type: Number,
        default: 10
    },
    options: {
        type: Array,
        default: null
    },
    includeSmallSizes: {
        type: Boolean,
        default: false
    },
    includeLargeSizes: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: '10/page'
    },
    loading: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    width: {
        type: String,
        default: 'auto'
    },
    maxWidth: {
        type: String,
        default: '12rem'
    },
    triggerClass: {
        type: String,
        default: ''
    }
})

const emit = defineEmits(['update:modelValue'])

const pageOptions = computed(() => {
    // Use custom options if provided
    if (props.options?.length) {
        return props.options.map(option =>
            typeof option === 'number'
                ? { value: option, label: `${option}/page` }
                : option
        )
    }

    // Build standard options
    let sizes = [...PAGE_SIZES.STANDARD]

    if (props.includeSmallSizes) {
        sizes = [...PAGE_SIZES.SMALL, ...sizes.filter(s => !PAGE_SIZES.SMALL.includes(s))]
    }

    if (props.includeLargeSizes) {
        sizes = [...sizes, ...PAGE_SIZES.LARGE.filter(s => !sizes.includes(s))]
    }

    return sizes.map(size => ({
        value: size,
        label: `${size}/page`
    }))
})

const handleChange = (value) => {
    emit('update:modelValue', parseInt(value))
}
</script>